import { ApiError } from "@/api/client";

/**
 * What a PERSON should read when a load fails.
 *
 * WHY THIS EXISTS. `ApiError.message` is built as
 * `${response.status} ${response.statusText}: ${body}` — protocol text, by construction,
 * at every throw site in `api/client.ts`. Seven hooks stored that string straight into
 * their `error` state and the screens rendered it as the detail line under
 * "Can't reach the demo API right now."
 *
 * MEASURED 2026-09-06 on the Live Wall, by stopping the API so Caddy answered 502:
 *
 *     Recent activity — "Can't reach the demo API right now. 502 Bad Gateway"
 *     live regions:    [..., "Can't reach the demo API right now. 502 Bad Gateway"]
 *
 * On the laptop that stands in front of prospects all day, in a `role="status"` region, so
 * a screen reader announced "502 Bad Gateway" as well.
 *
 * This is the defect `of-002c` already fixed once, on the phone: *"With the db container
 * stopped and the API still up, submitting put '500 Internal Server Error' on a farmer's
 * phone, in a live region so a screen reader announced it too. That is protocol text, and
 * it tells them nothing they can act on."* The submit path was fixed; every LOAD path was
 * not, on either surface.
 *
 * THE TECHNICAL DETAIL IS NOT LOST — the failing request, its status and its body are in
 * the browser's network panel, which is where someone diagnosing it is looking anyway. A
 * booth screen is not that place.
 *
 * DELIBERATELY SAYS NOTHING ABOUT RETRYING. The entries feed does keep polling; a one-shot
 * report load does not. A single sentence claiming "it will keep trying" would be false on
 * half its call sites, and this session has spent most of its time on screens that assert
 * things the system cannot support.
 */
export function describeReadFailure(err: unknown): string {
  if (err instanceof ApiError) {
    // 0 / 502 / 504 — the request never reached the application, so nothing here is a
    // statement about what EasyFarm did with it.
    if (err.neverReachedTheServer) return "The request didn’t reach EasyFarm.";
    if (err.status === 401 || err.status === 403) return "You’re signed out.";
    return "EasyFarm couldn’t load this.";
  }
  return "Couldn’t load this.";
}
